import type { PlayerId, TeamId } from '@game/shared';

/**
 * Voice chat. Audio flows peer-to-peer over WebRTC — browser-native Opus,
 * echo cancellation and jitter buffering for free, and no audio load on the
 * game server. The server's only role is relaying the signalling frames
 * (offer/answer/ICE) between players in the same match, which is what scopes
 * voice to the room: the lobby talks to the lobby, an arena to its arena.
 *
 * A full mesh is built between everyone in the room as soon as they meet,
 * whether or not anyone is speaking. That makes the mic toggle instant — a
 * `replaceTrack` on an audio slot negotiated up front, no renegotiation —
 * and an idle connection costs next to nothing. Rooms are small (a match is
 * at most a handful of players), so the mesh never grows past that.
 *
 * The mic is never persisted and starts muted every session. Incoming voice
 * is filtered by a persisted setting (everyone / team only / off).
 */

/** Which remote players are audible. Persisted in settings. */
export type VoiceReceiveMode = 'everyone' | 'team' | 'off';

/**
 * The audibility rule, pure for tests. In the lobby (`combat` false) there
 * are no sides, so "team only" hears everyone there.
 */
export const voiceAudible = (
  mode: VoiceReceiveMode,
  sameTeam: boolean,
  combat: boolean,
): boolean => {
  if (mode === 'off') return false;
  if (mode === 'team') return !combat || sameTeam;
  return true;
};

/**
 * Public STUN is enough for home NATs; on the same LAN the host candidates
 * connect directly. There is deliberately no TURN server yet — a symmetric
 * NAT pair would fail to connect and simply not hear each other.
 */
const RTC_CONFIG: RTCConfiguration = {
  iceServers: [
    { urls: ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'] },
  ],
};

/** What rides the signalling relay, as JSON. */
interface SignalPayload {
  kind: 'offer' | 'answer' | 'ice';
  sdp?: string;
  candidate?: RTCIceCandidateInit;
}

interface VoicePeer {
  connection: RTCPeerConnection;
  /** The pre-negotiated outgoing audio slot; the mic track swaps in and out. */
  sender: RTCRtpSender;
  /** Playback sink. Never in the DOM — an element is what makes audio flow. */
  audio: HTMLAudioElement;
  team: TeamId;
  /** Candidates that arrived before the remote description; flushed after. */
  pendingIce: RTCIceCandidateInit[];
  haveRemote: boolean;
}

export class VoiceManager {
  private readonly peers = new Map<PlayerId, VoicePeer>();
  private localId: PlayerId = 0;
  private localTeam: TeamId = 0;
  private combat = false;
  private mode: VoiceReceiveMode = 'everyone';
  private micStream: MediaStream | null = null;
  /** The player's intent, distinct from the stream: getUserMedia is async. */
  private micWanted = false;

  constructor(private readonly sendSignal: (to: PlayerId, data: string) => void) {}

  /**
   * A Welcome arrived — this player is in a (possibly different) room now.
   * Every peer belongs to the old room and is torn down; the new room's
   * roster arrives as PlayerJoined messages right after. The mic stream is
   * kept: a mic turned on in the lobby stays on through a transfer.
   */
  roomChanged(localId: PlayerId, localTeam: TeamId, combat: boolean): void {
    this.reset();
    this.localId = localId;
    this.localTeam = localTeam;
    this.combat = combat;
  }

  reset(): void {
    for (const peer of this.peers.values()) this.closePeer(peer);
    this.peers.clear();
  }

  dispose(): void {
    this.reset();
    void this.setMicEnabled(false);
  }

  addPeer(id: PlayerId, team: TeamId): void {
    if (id === this.localId || this.peers.has(id)) return;
    if (typeof RTCPeerConnection === 'undefined') return;

    const connection = new RTCPeerConnection(RTC_CONFIG);
    // One audio slot each way, negotiated before anyone speaks: turning the
    // mic on later is a replaceTrack on this sender, never a renegotiation.
    const transceiver = connection.addTransceiver('audio', { direction: 'sendrecv' });
    const micTrack = this.micStream?.getAudioTracks()[0];
    if (micTrack) void transceiver.sender.replaceTrack(micTrack);

    const audio = new Audio();
    audio.autoplay = true;
    const peer: VoicePeer = {
      connection,
      sender: transceiver.sender,
      audio,
      team,
      pendingIce: [],
      haveRemote: false,
    };
    audio.muted = !this.audible(peer);
    this.peers.set(id, peer);

    connection.ontrack = (event) => {
      audio.srcObject = event.streams[0] ?? new MediaStream([event.track]);
      audio.muted = !this.audible(peer);
      // The click that joined the game already satisfied the autoplay gesture
      // rule; a rejection here just means silence, never a crash.
      void audio.play().catch(() => {});
    };
    connection.onicecandidate = (event) => {
      if (!event.candidate) return;
      this.signal(id, { kind: 'ice', candidate: event.candidate.toJSON() });
    };

    // Exactly one side may offer or both would (signalling glare). The lower
    // player id offers — both sides know both ids, so no coordination needed.
    if (this.localId < id) {
      void connection
        .createOffer()
        .then(async (offer) => {
          await connection.setLocalDescription(offer);
          if (offer.sdp) this.signal(id, { kind: 'offer', sdp: offer.sdp });
        })
        .catch(() => {});
    }
  }

  removePeer(id: PlayerId): void {
    const peer = this.peers.get(id);
    if (!peer) return;
    this.peers.delete(id);
    this.closePeer(peer);
  }

  handleSignal(from: PlayerId, data: string): void {
    // Signals can only arrive after the roster introduced the sender (the
    // socket is ordered), so an unknown peer is junk, not a race.
    const peer = this.peers.get(from);
    if (!peer) return;
    let payload: SignalPayload;
    try {
      payload = JSON.parse(data) as SignalPayload;
    } catch {
      return;
    }
    void this.applySignal(from, peer, payload).catch(() => {});
  }

  /**
   * Turns the microphone on or off. Off always succeeds and fully releases
   * the device, so the browser's recording indicator goes dark. On resolves
   * false when the user denies the permission prompt (or the context has no
   * getUserMedia — plain-http LAN pages).
   */
  async setMicEnabled(on: boolean): Promise<boolean> {
    this.micWanted = on;
    if (!on) {
      if (this.micStream) {
        for (const track of this.micStream.getTracks()) track.stop();
        this.micStream = null;
      }
      for (const peer of this.peers.values()) void peer.sender.replaceTrack(null);
      return true;
    }

    if (this.micStream) return true;
    if (!navigator.mediaDevices?.getUserMedia) return false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      // The toggle may have flipped off while the permission prompt was up.
      if (!this.micWanted) {
        for (const track of stream.getTracks()) track.stop();
        return false;
      }
      this.micStream = stream;
      const track = stream.getAudioTracks()[0] ?? null;
      for (const peer of this.peers.values()) void peer.sender.replaceTrack(track);
      return true;
    } catch {
      return false;
    }
  }

  setReceiveMode(mode: VoiceReceiveMode): void {
    this.mode = mode;
    for (const peer of this.peers.values()) peer.audio.muted = !this.audible(peer);
  }

  private audible(peer: VoicePeer): boolean {
    return voiceAudible(this.mode, peer.team === this.localTeam, this.combat);
  }

  private signal(to: PlayerId, payload: SignalPayload): void {
    this.sendSignal(to, JSON.stringify(payload));
  }

  private async applySignal(
    from: PlayerId,
    peer: VoicePeer,
    payload: SignalPayload,
  ): Promise<void> {
    const pc = peer.connection;
    if (payload.kind === 'offer' && typeof payload.sdp === 'string') {
      await pc.setRemoteDescription({ type: 'offer', sdp: payload.sdp });
      peer.haveRemote = true;
      await this.flushIce(peer);
      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      if (answer.sdp) this.signal(from, { kind: 'answer', sdp: answer.sdp });
    } else if (payload.kind === 'answer' && typeof payload.sdp === 'string') {
      await pc.setRemoteDescription({ type: 'answer', sdp: payload.sdp });
      peer.haveRemote = true;
      await this.flushIce(peer);
    } else if (payload.kind === 'ice' && payload.candidate) {
      if (peer.haveRemote) await pc.addIceCandidate(payload.candidate).catch(() => {});
      else peer.pendingIce.push(payload.candidate);
    }
  }

  private async flushIce(peer: VoicePeer): Promise<void> {
    for (const candidate of peer.pendingIce.splice(0)) {
      await peer.connection.addIceCandidate(candidate).catch(() => {});
    }
  }

  private closePeer(peer: VoicePeer): void {
    peer.connection.ontrack = null;
    peer.connection.onicecandidate = null;
    peer.connection.close();
    peer.audio.srcObject = null;
    peer.audio.pause();
  }
}
